import React, { useState, useEffect } from 'react'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Divider, Grid, Switch, Typography, useTheme, Collapse, List, ListItem, ListItemText } from '@mui/material'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from '../features-tab/style'
import { useTenantFormContext } from '../tenant-form-context'
import { Controller } from 'react-hook-form'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'

const SkillBitesSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant, isEdit, form } = useTenantFormContext()
  const { watch, setValue } = form
  const [isLoading, setIsLoading] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const skillBite = watch('features.skillBite')

  const handleSwitch = () => {
    setValue('features.skillBite', { enabled: !skillBite?.enabled })
  }

  useEffect(() => {
    if (!isEdit) {
      setValue('features.skillBite', { enabled: true, flexibleScheduling: false, credlyBadges: false })
      return
    }
    const hasSkillBites = tenant?.features?.skillBite?.enabled
    const isFlexibleScheduling = tenant?.features?.skillBite?.flexibleScheduling
    const areCredlyBadgesEnabled = tenant?.features?.skillBite?.credlyBadges
    setValue('features.skillBite', {
      enabled: hasSkillBites,
      flexibleScheduling: isFlexibleScheduling,
      credlyBadges: areCredlyBadgesEnabled
    })
  }, [tenant, isEdit, setValue])

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Skill Bites configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the Skill Bites feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSkillBitesSettings = () => setIsCollapsed(isCollapsed => !isCollapsed)

  const handleFlexibleSchedulingSwitch = (event) => {
    setValue('features.skillBite.flexibleScheduling', event.target.checked)
  }

  const handleCredlyBadgesSwitch = (event) => {
    setValue('features.skillBite.credlyBadges', event.target.checked)
  }

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        skillBite: skillBite
      }
    })
    setIsLoading(true)
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
            <Typography variant='body2' sx={{ mt: spacing(3.5) }}>
              Skill Bites
            </Typography>
            <Box display='flex' justifyContent='flex-end' alignItems='center' padding={spacing(1)}>
              {skillBite?.enabled && !isLoading && isEdit && (
                <>
                  <Box
                    visibility={skillBite?.enabled ? 'visible' : 'hidden'}
                    sx={{
                      ':hover': {
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <List component='nav' aria-label='Device settings'>
                      <ListItem onClick={handleSkillBitesSettings} aria-haspopup='true' aria-controls='skill-bites-settings-menu' aria-label='Skill Bites Settings'>
                        <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant='body2'>Skill Bites Settings</Typography>
                          {isCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                  <Divider variant='middle' flexItem orientation='vertical' sx={{ mx: spacing(2), borderColor: 'rgba(0, 0, 0, 0.38)' }} />
                </>
              )}
              <Controller
                name='features.skillBite.enabled'
                control={form.control}
                render={({ field }) =>
                  <Switch
                    color='primary'
                    checked={!!field.value}
                    onChange={handleSwitch}
                    disabled={isLoading}
                    data-test='skill-bites-product-toggle'
                    {...field}
                  />}
              />
            </Box>
          </Box>

          <Collapse in={isCollapsed}>
            {skillBite?.enabled && !isLoading && isEdit && (
              <>
                <Box display='flex' flexDirection='column' gap={2} mb={spacing(1)}>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box width='50%'>
                      <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                        Flexible Session Scheduling
                      </Typography>
                      <Typography variant='body2' sx={{ mt: spacing(1) }}>
                        Enable flexible scheduling with instant access to Skill Bites sessions.
                        Users can unlock sessions immediately and create their own personalized
                        learning schedule at their preferred pace by configuring their email nudge dates.
                      </Typography>
                    </Box>
                    <Switch
                      color='primary'
                      checked={skillBite?.flexibleScheduling}
                      onChange={handleFlexibleSchedulingSwitch}
                      disabled={!skillBite}
                      data-test='skill-bites-flexible-scheduling-toggle'
                    />
                  </Box>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box width='50%'>
                      <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                        Credly Badges
                      </Typography>
                      <Typography variant='body2' sx={{ mt: spacing(1) }}>
                        Enable Credly badge issuance when users complete the final session of a
                        Skill Bite. Earners will be able to view their badge on Credly and share
                        it to their LinkedIn profile. Requires a Credly badge template to be
                        configured on each Skill Bite.
                      </Typography>
                    </Box>
                    <Switch
                      color='primary'
                      checked={!!skillBite?.credlyBadges}
                      onChange={handleCredlyBadgesSwitch}
                      disabled={!skillBite}
                      data-test='skill-bites-credly-badges-toggle'
                    />
                  </Box>
                </Box>
              </>
            )}
          </Collapse>

          {isEdit && (
            <Box>
              <Divider style={{ marginBottom: spacing(1) }} />

              <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
                <ActionButton
                  onClick={handleSave}
                  disabled={isLoading}
                  variant='primary'
                  dataTest='skill-bites-save-button'
                  style={{ height: 'min-content' }}
                > Save
                </ActionButton>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default SkillBitesSettings
